import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CONSENT_CHECK_TYPES } from '../../utils/constants';
import Alert from '../common/Alert';
import './ConsentForm.css';

const ConsentForm = ({ onSubmit, isSubmitting }) => {
  const [consents, setConsents] = useState(
    Object.keys(CONSENT_CHECK_TYPES).reduce((acc, key) => {
      acc[key] = false; // explicitly start as unchecked
      return acc;
    }, {})
  );

  const handleToggle = (key) => {
    setConsents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const hasMandatory = Object.entries(CONSENT_CHECK_TYPES)
    .filter(([_, type]) => type.mandatory)
    .every(([key, _]) => consents[key] === true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasMandatory) return;
    
    const formattedConsents = Object.keys(consents).map(key => ({
      type: key,
      granted: consents[key]
    }));
    onSubmit(formattedConsents);
  };

  return (
    <form className="consent-form card" onSubmit={handleSubmit}>
      <div className="card-header">
        <h2>Provide Your Consents</h2>
      </div>
      <div className="card-body">
        <p className="body-text consent-intro">
          Please review and explicitly grant consent for the following checks. 
          None are pre-selected. You must grant the mandatory checks to proceed with the application.
        </p>

        {!hasMandatory && (
          <Alert type="warning" message="You must select all mandatory consents to continue." />
        )}

        <fieldset className="consent-fieldset">
          <legend className="sr-only">Consent Options</legend>
          
          {Object.entries(CONSENT_CHECK_TYPES).map(([key, type]) => (
            <div key={key} className={`consent-option ${type.mandatory ? 'consent-option--mandatory' : 'consent-option--optional'}`}>
              <label className="consent-option__label">
                <input
                  type="checkbox"
                  checked={consents[key]}
                  onChange={() => handleToggle(key)}
                  className="consent-option__checkbox"
                  aria-describedby={`desc-${key}`}
                />
                <div className="consent-option__content">
                  <div className="consent-option__header">
                    <span className="consent-option__title">{type.label}</span>
                    {type.mandatory ? (
                      <span className="badge badge--mandatory">Mandatory</span>
                    ) : (
                      <span className="badge badge--optional">Optional</span>
                    )}
                  </div>
                  <p id={`desc-${key}`} className="consent-option__desc">{type.description}</p>
                </div>
              </label>
            </div>
          ))}
        </fieldset>

        <div className="consent-refusal-notice">
          <p className="helper-text">
            <strong>Note:</strong> Refusing optional consents may limit the landlord's ability to fully assess your application, potentially affecting your chances of approval.
          </p>
        </div>

        <div className="consent-form__actions">
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={!hasMandatory || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Confirm Consents & Proceed'}
          </button>
        </div>
      </div>
    </form>
  );
};

ConsentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool
};

export default ConsentForm;
